import React, { useEffect, useMemo, useState } from 'react';
import { Container, Card, CardBody } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import {
  FaTachometerAlt,
  FaBoxes,
  FaShoppingCart,
  FaShoppingBasket,
  FaCog,
  FaUserCircle,
  FaSignOutAlt,
  FaSave,
  FaSun,
  FaMoon,
  FaLayerGroup
} from 'react-icons/fa';
import { authActions } from '../../sagas/authSlice';
import './ModuleSelector.css';
import { ROLE_SUPER_ADMIN } from '../../helpers/constants';

const ModuleSelector = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fullName, businessName, role } = useSelector(state => state.auth);
  const [darkMode, setDarkMode] = useState(false);

  const modules = useMemo(() => [
    {
      id: 'dashboard',
      title: t('modules.dashboard.title'),
      description: t('modules.dashboard.description'),
      icon: FaTachometerAlt,
      color: '#2563eb',
      route: '/dashboard'
    },
    {
      id: 'inventory',
      title: t('modules.inventory.title'),
      description: t('modules.inventory.description'),
      icon: FaBoxes,
      color: '#059669',
      route: '/inventory-module'
    },
    {
      id: 'pos',
      title: 'Point de Vente',
      description: 'Ventes comptoir, caisse et encaissements rapides',
      icon: FaShoppingCart,
      color: '#db2777',
      route: '/comptoire',
      primary: true
    },
    {
      id: 'sales',
      title: t('modules.sales.title'),
      description: t('modules.sales.description'),
      icon: FaShoppingCart,
      color: '#0891b2',
      route: '/sales-module'
    },
    {
      id: 'purchases',
      title: t('modules.purchases.title'),
      description: t('modules.purchases.description'),
      icon: FaShoppingBasket,
      color: '#ea580c',
      route: '/purchases-module'
    },
    {
      id: 'dala',
      title: t('modules.dala.title'),
      description: t('modules.dala.description'),
      icon: FaLayerGroup,
      color: '#7c3aed',
      route: '/dala-module'
    },
    {
      id: 'backups',
      title: t('modules.backups.title'),
      description: t('modules.backups.description'),
      icon: FaSave,
      color: '#0f766e',
      route: '/backups-module'
    },
    {
      id: 'settings',
      title: t('modules.settings.title'),
      description: t('modules.settings.description'),
      icon: FaCog,
      color: '#475569',
      route: '/settings-module'
    }
  ].filter(module =>
    (module.id !== 'settings' && module.id !== 'dashboard' && module.id !== 'backups') || role === ROLE_SUPER_ADMIN
  ), [role, t]);

  useEffect(() => {
    const onKey = (e) => {
      const idx = Number(e.key) - 1;
      if (idx >= 0 && idx < modules.length) {
        navigate(modules[idx].route);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modules, navigate]);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    return () => document.body.classList.remove('dark-mode');
  }, [darkMode]);

  const handleLogout = () => {
    Swal.fire({
      title: 'Confirmer la deconnexion',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Se deconnecter',
      cancelButtonText: 'Annuler'
    }).then((r) => {
      if (r.isConfirmed) {
        dispatch(authActions.logout());
        localStorage.clear();
        navigate('/login');
      }
    });
  };

  return (
    <div className="module-selector-wrapper">
      <header className="modules-topbar">
        <Container fluid className="modules-topbar-inner">
          <div className="brand-block">
            <div className="brand-mark">{businessName?.slice(0, 2)?.toUpperCase() || 'MP'}</div>
            <div>
              <strong>{businessName}</strong>
              <span>ERP & POS</span>
            </div>
          </div>

          <div className="topbar-actions">
            <button className="theme-toggle" onClick={() => setDarkMode(v => !v)} type="button">
              {darkMode ? <FaSun /> : <FaMoon />}
            </button>

            <div className="user-pill">
              <FaUserCircle />
              <span>{fullName}</span>
            </div>

            <button className="logout-btn" onClick={handleLogout} type="button">
              <FaSignOutAlt />
              <span>Deconnexion</span>
            </button>
          </div>
        </Container>
      </header>

      <main className="modules-main">
        <Container fluid className="modules-shell">
          <section className="modules-hero-panel">
            <div>
              <span className="hero-kicker">Espace de travail</span>
              <h1>{t('modules.welcome', { businessName })}</h1>
              <p>Accedez aux domaines operationnels et administratifs depuis un portail unique.</p>
            </div>

            <div className="hero-meta">
              <div>
                <strong>{modules.length}</strong>
                <span>Modules actifs</span>
              </div>
              <div>
                <strong>{role === ROLE_SUPER_ADMIN ? 'Admin' : 'User'}</strong>
                <span>Profil connecte</span>
              </div>
            </div>
          </section>

          <section className="modules-grid" aria-label="Modules">
            {modules.map((module, index) => (
              <Card
                key={module.id}
                className={`module-card-dense ${module.primary ? 'primary' : ''}`}
                onClick={() => navigate(module.route)}
                style={{ '--module-accent': module.color }}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(module.route);
                  }
                }}
              >
                <CardBody className="module-card-body-dense">
                  <div className="module-card-top">
                    <span className="module-icon-wrap">
                      <module.icon size={22} />
                    </span>
                    <span className="shortcut">{index + 1}</span>
                  </div>
                  <div>
                    <div className="module-title-dense">{module.title}</div>
                    <p>{module.description}</p>
                  </div>
                </CardBody>
              </Card>
            ))}
          </section>
        </Container>
      </main>

      <footer className="app-footer">
        <Container fluid className="footer-content">
          <span><strong>{businessName}</strong> - ERP & POS</span>
          <span>{new Date().getFullYear()}</span>
          <span>Powered by <strong>IDERACLOUD</strong></span>
        </Container>
      </footer>
    </div>
  );
};

export default ModuleSelector;
